import { DefaultError, useQuery } from '@tanstack/react-query';
import { IInfinitePageResponse } from 'services/types';
import { apiClient } from 'utils';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  IEarnings,
  ILabelEarnings,
  ISalesChannelWithProviderEarning,
  IStatisticsQuery,
  ITopActiveArtist,
  ITopLabelReleasesCount,
  ITopProviders,
  ITopSalesChannels,
  ITopTrackEarnings,
} from './types';

export { useGetSpotifyStatistics } from './spotify';
export type {
  ISpotifyStatisticsQuery,
  ISpotifyStatisticsResponse,
  ISpotifyStatisticsData,
  ITrackAggregatedStats,
  IStreamsByCategory,
  IStreamsByCountryGender,
  IStreamsByGenderAge,
  IStreamsByCountrySexAge,
  ISavesByCountryProduct,
} from './types';

export const useGetEarnings = (query: IStatisticsQuery, enabled?: boolean) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IEarnings[], DefaultError>({
    queryKey: ['top-earnings', query],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Statistics/Top/Earnings`,
        params: {
          StartSettlementPeriod: query.startSettlementPeriodId,
          EndSettlementPeriod: query.endSettlementPeriodId,
        },
      });

      return responseHandler(response, {
        groupName: `useGetEarnings`,
        onNotFound: () => {},
      });
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useGetTopProviders = (
  query: IStatisticsQuery,
  enabled?: boolean
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ITopProviders[], DefaultError>({
    queryKey: ['top-providers', query],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Statistics/Top/Providers`,
        params: query,
      });

      return responseHandler(response, {
        groupName: `useGetTopProviders`,
        onNotFound: () => {},
      });
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};
export const useGetTopSalesChannels = (
  query: IStatisticsQuery,
  enabled?: boolean
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ITopSalesChannels[], DefaultError>({
    queryKey: ['top-sales-channels', query],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Statistics/Top/SalesChannels`,
        params: query,
      });

      return responseHandler(response, {
        groupName: `useGetTopSalesChannels`,
        onNotFound: () => {},
      });
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useGetTopLabels = (query: IStatisticsQuery, enabled?: boolean) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ILabelEarnings[], DefaultError>({
    queryKey: ['top-labels-earnings', query],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Statistics/Top/Labels`,
        params: {
          StartSettlementPeriod: query.startSettlementPeriodId,
          EndSettlementPeriod: query.endSettlementPeriodId,
        },
      });

      return responseHandler(response, {
        groupName: `useGetLabelsEarnings`,
        onNotFound: () => {},
      });
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useGetTopActiveArtists = (
  query: IStatisticsQuery,
  enabled?: boolean
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ITopActiveArtist[], DefaultError>({
    queryKey: ['top-active-artists', query],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Statistics/Top/ActiveArtists`,
        params: {
          startSettlementPeriodId: query.startSettlementPeriodId,
          endSettlementPeriodId: query.endSettlementPeriodId,
        },
      });

      return responseHandler(response, {
        groupName: `useGetTopActiveArtists`,
        onNotFound: () => {},
      });
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useGetTopLabelReleasesCount = (
  query: IStatisticsQuery,
  enabled?: boolean
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ITopLabelReleasesCount[], DefaultError>({
    queryKey: ['top-label-releases-count', query],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Statistics/Top/Label/ReleasesCount`,
        params: {
          startSettlementPeriodId: query.startSettlementPeriodId,
          endSettlementPeriodId: query.endSettlementPeriodId,
        },
      });

      return responseHandler(response, {
        groupName: `useGetTopLabelReleasesCount`,
        onNotFound: () => {},
      });
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useGetSalesChannelsWithProviderEarnings = (
  query: IStatisticsQuery,
  enabled?: boolean
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ISalesChannelWithProviderEarning[], DefaultError>({
    queryKey: ['top-sales-channel-with-provider-earnings', query],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Statistics/Top/SalesChannelsWithProviders`,
        params: query,
      });

      return responseHandler(response, {
        groupName: `useGetSalesChannelsWithProviderEarnings`,
        onNotFound: () => {},
      });
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};
export const useGetTopTracksEarnings = (
  query: IStatisticsQuery,
  enabled?: boolean
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ITopTrackEarnings[], DefaultError>({
    queryKey: ['top-tracks-earnings', query],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Statistics/Top/Tracks`,
        params: {
          StartSettlementPeriod: query.startSettlementPeriodId,
          EndSettlementPeriod: query.endSettlementPeriodId,
          page: 0,
          ItemCount: 10,
        },
      });

      const { items } = await responseHandler<
        IInfinitePageResponse<ITopTrackEarnings>
      >(response, {
        groupName: `useGetTopTracksEarnings`,
        onNotFound: () => {},
      });

      return items;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};
